import { requestJson, assertOk} from "./storeApi"

function parseNumber(value, fallback = 0) {
	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function parseInteger(value, fallback = 0) {
	const parsedValue = Number(value);
	if (!Number.isFinite(parsedValue) || !Number.isInteger(parsedValue)) {
		return fallback;
	}

	return parsedValue;
}

function normalizeVariantAttributes(attributes) {
	const normalizedAttributes = Array.isArray(attributes) ? attributes : [];

	return normalizedAttributes
		.map((attribute) => ({
			name: String(attribute?.Name || attribute?.name || "").trim(),
			value: String(attribute?.Value || attribute?.value || "").trim(),
			...normalizeAttributeColor(attribute),
		}))
		.filter((attribute) => attribute.name && attribute.value);
}

function normalizeAttributeColor(attribute) {
	const colorCode = attribute?.color_code ?? attribute?.colorCode ?? attribute?.ColorCode;
	return colorCode == null ? {} : { color_code: String(colorCode).trim() };
}

function normalizeVariantImages(images) {
	const normalizedImages = Array.isArray(images) ? images : [];

	return normalizedImages
		.map((image) => {
			if (typeof image === "string") {
				const url = image.trim();
				return { url, alt_text: "" };
			}

			const url = String(image?.URL || image?.url || "").trim();
			const altText = String(image?.AltText || image?.alt_text || image?.altText || "").trim();
			return { url, alt_text: altText };
		})
		.filter((image) => image.url && /^https?:\/\//i.test(image.url));
}

function normalizeVariant(variant) {
	const sku = String(variant?.SKU || variant?.sku || "").trim();
	const price = parseNumber(variant?.Price ?? variant?.price, 0);
	const cost = parseNumber(variant?.Cost ?? variant?.cost, 0);
	const salePrice = parseNumber(variant?.SalePrice ?? variant?.sale_price, 0);
	const stock = parseInteger(variant?.Stock ?? variant?.stock, 0);
	const weight = parseNumber(variant?.Weight ?? variant?.weight, 0);

	if (!sku) {
		return { variant: null, error: "Each variant requires an SKU." };
	}

	if (price < 0 || cost < 0) {
		return { variant: null, error: `Variant ${sku}: price and cost must be non-negative.` };
	}

	if (!Number.isInteger(Number(variant?.Stock ?? variant?.stock ?? 0))) {
		return { variant: null, error: `Variant ${sku}: stock must be an integer.` };
	}

	return {
		variant: {
			sku,
			price,
			sale_price: salePrice,
			cost,
			weight,
			stock,
			active: Boolean(variant?.Active ?? variant?.active ?? true),
			attributes: normalizeVariantAttributes(variant?.Attributes ?? variant?.attributes),
			images: normalizeVariantImages(variant?.Images ?? variant?.images),
		},
		error: null,
	};
}

export function buildCreateProductWithVariantsPayload(product) {
	const name = String(product?.name || "").trim();
	const description = String(product?.description || "").trim();
	const rawVariants = Array.isArray(product?.variants) ? product.variants : [];

	if (!name) {
		throw new Error("Product name is required.");
	}

	if (!description) {
		throw new Error("Product description is required.");
	}

	if (rawVariants.length === 0) {
		throw new Error("At least one variant is required.");
	}

	const seenSkus = new Set();
	const normalizedVariants = [];

	rawVariants.forEach((variant) => {
		const { variant: normalizedVariant, error } = normalizeVariant(variant);
		if (error) {
			throw new Error(error);
		}

		const normalizedSku = normalizedVariant.sku.toLowerCase();
		if (seenSkus.has(normalizedSku)) {
			return;
		}

		seenSkus.add(normalizedSku);
		normalizedVariants.push(normalizedVariant);
	});

	if (normalizedVariants.length === 0) {
		throw new Error("At least one unique variant is required.");
	}

	return {
		name,
		description,
		images: [],
		variants: normalizedVariants,
	};
}

export async function getProducts() {
    const { response, payload } = await requestJson("/products", {
        method: "GET",
    });

    return assertOk(response, payload, "Failed to load products");
}

export async function getProduct(productId) {
    const { response, payload } = await requestJson(`/products/${productId}`, {
        method: "GET",
    });

    return assertOk(response, payload, "Failed to load product");
}

export async function getProductVariants(productId) {
    const { response, payload } = await requestJson(`/products/${productId}/variant`, {
        method: "GET",
    });

    return assertOk(response, payload, "Failed to load product variants");
}

export async function getProductVariantQuickStats(variantId) {
    const { response, payload } = await requestJson(`/products/variant/${variantId}/quickStats`, {
        method: "GET",
    });

    return assertOk(response, payload, "Failed to load variant stats");
}

export async function getProductVariantImages(variantId) {
    const { response, payload } = await requestJson(`/products/${variantId}/images`, {
        method: "GET",
    });

    return assertOk(response, payload, "Failed to load variant images");
}

export async function deleteProductImage(variantId, imageId) {
    const { response, payload } = await requestJson(`/products/${variantId}/images/${imageId}`, {
        method: "DELETE",
    });

    if (response.status === 204) {
        return true;
    }

    return assertOk(response, payload, "Failed to delete product image");
}

export async function updateProductImage(variantId, imageId, imageUpdate) {
    const { response, payload } = await requestJson(`/products/${variantId}/images/${imageId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            alt_text: imageUpdate?.alt_text ?? imageUpdate?.altText ?? "",
            media_link: imageUpdate?.media_link ?? imageUpdate?.mediaLink ?? "",
        }),
    });

    return assertOk(response, payload, "Failed to update product image");
}

export async function createProduct(product) {
	const { response, payload } = await requestJson("/products", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(product),
	});

	return assertOk(response, payload, "Failed to create product");
}

export async function createProductWithVariants(product) {
	const payload = buildCreateProductWithVariantsPayload(product);
	return createProduct(payload);
}

export async function createVariantsForProduct(productId, variants) {
	const normalizedVariants = Array.isArray(variants) ? variants : [];
	if (normalizedVariants.length === 0) {
		return { variants: [] };
	}

	const payload = {
		variants: normalizedVariants.map((variant) => ({
			sku: String(variant?.SKU || variant?.sku || "").trim(),
			price: Number(variant?.Price ?? variant?.price ?? 0),
			sale_price: Number(variant?.SalePrice ?? variant?.sale_price ?? 0),
			cost: Number(variant?.Cost ?? variant?.cost ?? 0),
			weight: Number(variant?.Weight ?? variant?.weight ?? 0),
			stock: Number(variant?.Stock ?? variant?.stock ?? 0),
			active: Boolean(variant?.Active ?? variant?.active ?? true),
			attributes: normalizeVariantAttributes(variant?.Attributes ?? variant?.attributes),
			images: (Array.isArray(variant?.Images || variant?.images) ? (variant.Images || variant.images) : [])
				.map((img) => ({
					url: typeof img === "string" ? img : String(img?.URL || img?.url || "").trim(),
					alt_text: typeof img === "string" ? "" : String(img?.AltText || img?.alt_text || "").trim(),
				}))
				.filter((img) => img.url && !img.url.startsWith("data:")),
		}))
	};

	console.log("📡 POST /products/" + productId + "/variants with payload:", payload);

	const { response, payload: responsePayload } = await requestJson(`/products/${productId}/variants`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});

	console.log("📡 Response status:", response.status, "payload:", responsePayload);

	if (response.status === 201) {
		return responsePayload;
	}

	return assertOk(response, responsePayload, "Failed to create variants");
}

export async function editProduct(productId, productName, productDescription) {
	const { response, payload } = await requestJson(`/products/${productId}/edit`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			product_name: productName,
			product_description: productDescription,
		}),
	});

	if (response.status === 204) {
		return true;
	}

	return assertOk(response, payload, "Failed to update product");
}

export async function deleteProduct(productId) {
	const { response, payload } = await requestJson(`/products/${productId}`, {
		method: "DELETE",
	});

	return assertOk(response, payload, "Failed to delete product");
}

export async function deleteProductVariant(productId, variantId) {
	const { response, payload } = await requestJson(`/products/${productId}/variants/${variantId}`, {
		method: "DELETE",
	});

	return assertOk(response, payload, "Failed to delete variant");
}

export async function addProductVariantAttributes(productId, variantId, attributes) {
	const { response, payload } = await requestJson(
		`/products/${productId}/variants/${variantId}/attributes`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ attributes: normalizeVariantAttributes(attributes) }),
		}
	);

	return assertOk(response, payload, "Failed to add product attributes");
}

export async function updateProductVariantPrice(variantId, newPriceCents) {
	const { response, payload } = await requestJson(
		`/products/variant/${variantId}/update/price`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ new_price: newPriceCents }),
		}
	);

	return assertOk(response, payload, "Failed to update variant price");
}

export async function updateProductVariantAttribute(productId, variantId, attribute) {
	const { response, payload } = await requestJson(
		`/products/${productId}/variants/${variantId}/attributes`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				attribute_name: attribute?.attribute_name ?? attribute?.name ?? "",
				attribute_value: attribute?.attribute_value ?? attribute?.value ?? "",
				changed_attribute_id: attribute?.changed_attribute_id ?? attribute?.id ?? 0,
				...normalizeAttributeColor(attribute),
			}),
		}
	);

	return assertOk(response, payload, "Failed to update product attribute");
}

export async function uploadProductImage(productId, imageFile, options = {}) {
	const normalizedProductId = Number(productId);
	if (!Number.isFinite(normalizedProductId) || normalizedProductId <= 0) {
		throw new Error("A valid product id is required to upload an image.");
	}

	if (!(imageFile instanceof File)) {
		throw new Error("A valid image file is required.");
	}

	const formData = new FormData();
	formData.append("image", imageFile);

	const altText = String(options?.altText || "").trim();
	if (altText) {
		formData.append("alt_text", altText);
	}

	const variantId = Number(options?.variantId || 0);
	if (Number.isFinite(variantId) && variantId > 0) {
		formData.append("variant_id", String(variantId));
	}

	const { response, payload } = await requestJson(`/products/${normalizedProductId}/images`, {
		method: "POST",
		body: formData,
	});

	return assertOk(response, payload, "Failed to upload product image");
}